import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Menu, X, User, Calendar, LogOut, Settings, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { usePublicPageReady } from "@/components/PublicPageReadiness";

// Tree icon (high-res transparent PNG, icon only — no text)
const TREE_ICON_URL = "/FinalLogoTransparent.webp";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, loading: authLoading } = useAuth();
  const pageReady = usePublicPageReady();
  const location = useLocation();

  // Check if user is admin, super_admin, or therapist (staff)
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isTherapist = user?.role === "therapist";
  const isStaff = isAdmin || isTherapist;

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/about", label: "About" },
    { href: "/case-managers", label: "Case Managers" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <nav className="flex items-center justify-between" style={{ paddingTop: '6px', paddingBottom: '6px' }}>
          {/* Logo - Tree Icon + Styled Text */}
          <Link to="/" className="flex items-center gap-3 md:gap-3.5" data-testid="nav-logo">
            <img 
              src={TREE_ICON_URL} 
              alt="" 
              className="h-[54px] md:h-[66px] w-auto block flex-shrink-0"
              style={{ objectFit: 'contain' }}
            />
            <div className="flex flex-col justify-center">
              <span 
                className="text-[22px] md:text-[31px] leading-none"
                style={{ 
                  fontFamily: "'Playfair Display', serif", 
                  fontWeight: 600,
                  color: '#1F4D3A',
                }}
              >
                Growing Together
              </span>
              <span 
                className="text-[12px] md:text-[14px] leading-none mt-1.5"
                style={{ 
                  fontFamily: "'Inter', sans-serif", 
                  fontWeight: 500,
                  letterSpacing: '3.5px',
                  color: '#2F6B4F',
                }}
              >
                PHYSIOTHERAPY
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-[#1F4D3A]"
                    : "text-[#6B7280] hover:text-[#1F4D3A]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center justify-end w-48 h-11 shrink-0" data-testid="nav-book-slot">
            {authLoading ? <span className="w-full h-full" aria-hidden="true" /> : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 rounded-full max-w-full"
                    data-testid="user-menu-trigger"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isStaff ? 'bg-purple-100' : 'bg-[#E8F3E8]'}`}>
                      {isStaff ? (
                        <Shield className="w-4 h-4 text-purple-600" />
                      ) : (
                        <User className="w-4 h-4 text-[#2D4A3E]" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-[#2D4A3E] truncate">
                      {user.first_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl" data-testid="nav-user-menu">
                  {isStaff ? (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                          <Settings className="w-4 h-4" />
                          {isTherapist ? "My Dashboard" : "Admin Dashboard"}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-dashboard">
                          <Calendar className="w-4 h-4" />
                          My Bookings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-red-600 cursor-pointer"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : pageReady ? (
              <Link to="/booking" className="btn-primary text-sm w-full h-full inline-flex items-center justify-center whitespace-nowrap" data-testid="nav-book-cta">
                Book Appointment
              </Link>
            ) : <span className="w-full h-full" aria-hidden="true" />}
          </div>

          {/* Mobile Menu Toggle — meets Apple's 44×44 minimum */}
          <button
            className="lg:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg -mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#2D4A3E]" />
            ) : (
              <Menu className="w-6 h-6 text-[#2D4A3E]" />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#F2F0EB] animate-fade-in" data-testid="mobile-nav-menu">
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`block py-2 text-base font-medium ${
                  isActive(link.href) ? "text-[#2D4A3E]" : "text-[#6B7280]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#F2F0EB]">
              {authLoading ? <div className="h-12" aria-hidden="true" /> : user ? (
                <>
                  {isStaff ? (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-admin"
                      className="block py-2 text-base font-medium text-[#E09F7D]"
                    >
                      {isTherapist ? "My Dashboard" : "Admin Dashboard"}
                    </Link>
                  ) : (
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-dashboard"
                      className="block py-2 text-base font-medium text-[#2D4A3E]"
                    >
                      My Bookings
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="block py-2 text-base font-medium text-red-600"
                    data-testid="mobile-nav-logout"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col h-12" data-testid="mobile-nav-book-slot">
                  {pageReady && <Link to="/booking" onClick={() => setMobileMenuOpen(false)}
                    className="btn-primary w-full h-full inline-flex items-center justify-center" data-testid="mobile-nav-book-cta">
                    Book Appointment
                  </Link>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
